import {
  FEATURE_STATUS_COMPLETED,
  FEATURE_STATUS_FAILED,
  FEATURE_STATUS_PENDING,
  FEATURE_STATUS_RERUNNING,
} from "config/constant";
import { resolveEffectiveBudgetFeatureStatus } from "components/dashboard/body/projects/panels/budget/budgetAiRunState";
import type { ProjectStepDef, ProjectStepFeature } from "./projectNavigation";

/** What the sidebar shows next to a production step. */
export type ProjectStepStatus = "ready" | "running" | "not_started" | "failed";

/** Status names for tooltips and screen readers. Only Running and Failed show as text. */
export const PROJECT_STEP_STATUS_LABELS: Record<ProjectStepStatus, string> = {
  ready: "Ready",
  running: "Running",
  not_started: "Not generated yet",
  failed: "Failed",
};

/** One row of the project's `featureStatuses`, scoped to a document (draft). */
export interface ProjectFeatureStatusRow {
  feature: string;
  status: string;
  documentId?: string | null;
  metadata?: unknown;
}

type SeededPlaceholderMetadata = { seeded_placeholder?: boolean };

const findFeatureRow = (
  rows: readonly ProjectFeatureStatusRow[],
  feature: ProjectStepFeature,
  documentId: string | null | undefined
): ProjectFeatureStatusRow | undefined => {
  const candidates = rows.filter((row) => row.feature === feature);
  if (documentId) {
    const scoped = candidates.find((row) => String(row.documentId ?? "") === String(documentId));
    if (scoped) return scoped;
  }
  return candidates[0];
};

/**
 * Maps one feature status row to a step status.
 *
 * Backend facts this relies on (rivetai-backend `feature-status.ts`, `create-project.service.ts`):
 * - statuses are `pending`, `rerunning`, `completed`, `failed`, `cancelled`;
 * - a pipeline that never ran either has no row, or a `completed` row seeded with
 *   `metadata.seeded_placeholder: true` when the project was created;
 * - a budget run can end `completed` with an error in metadata, which the app treats as failed.
 */
export function mapFeatureStatusToStepStatus(
  row: ProjectFeatureStatusRow | undefined
): ProjectStepStatus {
  if (!row) return "not_started";

  const status =
    row.feature === "budget"
      ? resolveEffectiveBudgetFeatureStatus(row.status, row.metadata)
      : row.status;

  switch (status) {
    case FEATURE_STATUS_PENDING:
    case FEATURE_STATUS_RERUNNING:
      return "running";
    case FEATURE_STATUS_FAILED:
      return "failed";
    case FEATURE_STATUS_COMPLETED: {
      const metadata = row.metadata as SeededPlaceholderMetadata | null | undefined;
      return metadata?.seeded_placeholder === true ? "not_started" : "ready";
    }
    default:
      // `cancelled` and anything unknown: no finished run to show.
      return "not_started";
  }
}

/**
 * The status of a production step. Any of its pipelines running wins, then
 * any failed run, then the primary pipeline decides Ready or Not started.
 */
export function resolveProjectStepStatus(
  step: Pick<ProjectStepDef, "primaryFeature" | "secondaryFeatures">,
  rows: readonly ProjectFeatureStatusRow[] | null | undefined,
  activeDocumentId?: string | null
): ProjectStepStatus {
  const safeRows = rows ?? [];
  const features = [step.primaryFeature, ...step.secondaryFeatures];
  const statuses = features.map((feature) =>
    mapFeatureStatusToStepStatus(findFeatureRow(safeRows, feature, activeDocumentId))
  );

  if (statuses.includes("running")) return "running";
  if (statuses.includes("failed")) return "failed";
  return statuses[0] ?? "not_started";
}

export interface StepGenerateGateInput {
  status: ProjectStepStatus;
  /** The step is behind the Studio lock for this project. */
  locked: boolean;
  /** The user may run AI on this project (the Run AI flow is available). */
  canRunAi: boolean;
  /** The Run AI flow offers this step's pipeline for this project. */
  pipelineAvailable: boolean;
}

/**
 * An empty step page offers its Generate button only for a pipeline that
 * never ran, is not locked, whose user may run AI, and that the Run AI
 * dialog offers. The button only opens that dialog, where the user
 * confirms the run; it never starts one itself.
 */
export function canGenerateProjectStep({
  status,
  locked,
  canRunAi,
  pipelineAvailable,
}: StepGenerateGateInput): boolean {
  return status === "not_started" && !locked && canRunAi && pipelineAvailable;
}
